import {
  HTTP_INTERCEPTORS,
  HttpErrorResponse,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {ConfigService, retryWithBackoff} from '@q9elements/ui-kit/common';
import {get} from 'lodash';
import {catchError, EMPTY, tap, throwError} from 'rxjs';

import {SharedDialogService} from '../services/shared-dialog.service';

@Injectable()
class ErrorInterceptor implements HttpInterceptor {
  constructor(private sharedDialogService: SharedDialogService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler) {
    return next.handle(req).pipe(
      retryWithBackoff(),
      catchError(error => this.handlerError(error))
    );
  }

  handlerError(errorResponse: HttpErrorResponse) {
    const errorMessage = get(errorResponse, 'error.error', 'Unhandled error');
    const gracePeriodExpired = get(errorResponse, 'error.gracePeriodExpired', false);
    const gracePeriodNotified = get(errorResponse, 'error.gracePeriodNotified', false);

    if (this.isUnauthorizedError(errorResponse)) {
      return this.redirectToMainApp();
    }

    const isAppError = this.isAppError(errorResponse);
    const is2FAError = this.is2FAError(errorResponse);
    const showTwoFactorDialog = is2FAError && (gracePeriodExpired || !gracePeriodNotified);

    if (isAppError && !showTwoFactorDialog) {
      return this.sharedDialogService
        .showErrorDialog(errorMessage)
        .pipe(tap(() => this.redirectToMainApp()));
    } else if (showTwoFactorDialog) {
      return this.sharedDialogService
        .showTwoFactorDialog(gracePeriodExpired)
        .pipe(tap(() => window.location.reload()));
    }

    return throwError(errorMessage);
  }

  private redirectToMainApp() {
    const url = `${
      ConfigService.environment.MAIN_APP_URL
    }/signin?redirect=${encodeURIComponent(window.location.href)}`;

    window.location.replace(String(url));

    return EMPTY;
  }

  private isUnauthorizedError(errorResponse: HttpErrorResponse) {
    return errorResponse.status === 401;
  }

  private isAppError(errorResponse: HttpErrorResponse) {
    const {error} = errorResponse;
    const {http_code, status} = error;
    const statusCode = get(errorResponse, 'status', 0);
    const appErrors = ['user.err.user_is_not_sysadmin'];

    return (
      http_code === 401 || ((http_code === 403 || statusCode === 403) && appErrors.includes(status))
    );
  }

  private is2FAError(errorResponse: HttpErrorResponse) {
    const {error} = errorResponse;
    const {http_code, status} = error;
    const statusCode = get(errorResponse, 'status', 0);
    const twoFAError = 'two_factor_required';

    return (http_code === 403 || statusCode === 403) && twoFAError === status;
  }
}

export function provideErrorInterceptor() {
  return {
    provide: HTTP_INTERCEPTORS,
    useClass: ErrorInterceptor,
    multi: true
  };
}
